import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import CataloguePage from "@/pages/CataloguePage";
import DevisListPage from "@/pages/DevisListPage";
import DevisCreatePage from "@/pages/DevisCreatePage";
import DevisCreateSimplePage from "@/pages/DevisCreateSimplePage";
import DevisDetailPage from "@/pages/DevisDetailPage";
import DevisEditPage from "@/pages/DevisEditPage";
import DevisImportPage from "@/pages/DevisImportPage";
import UsersPage from "@/pages/UsersPage";
import CabinetsSNCPage from "@/pages/CabinetsSNCPage";
import FournisseursPage from "@/pages/FournisseursPage";
import AILeadPage from "@/pages/AILeadPage";
import EmailTemplatesPage from "@/pages/EmailTemplatesPage";
import PiecesRequisesPage from "@/pages/PiecesRequisesPage";
import ActivityReportPage from "@/pages/ActivityReportPage";
import SettingsPage from "@/pages/SettingsPage";
import WebsiteSettingsPage from "@/pages/WebsiteSettingsPage";
import MessagingPage from "@/pages/MessagingPage";
import CalendarPage from "@/pages/CalendarPage";
import TasksPage from "@/pages/TasksPage";
import FinancingPage from "@/pages/FinancingPage";
import MediaLibraryPage from "@/pages/MediaLibraryPage";
import SAVPage from "@/pages/SAVPage";
import MyProfilePage from "@/pages/MyProfilePage";
import BlogPage from "@/pages/BlogPage";
import EmailingPage from "@/pages/EmailingPage";
import EmailEditorPage from "@/pages/EmailEditorPage";
import AutomationRulesPage from "@/pages/AutomationRulesPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import ParcoursDashboardPage from "@/pages/ParcoursDashboardPage";
import RolesPermissionsPage from "@/pages/RolesPermissionsPage";
import AccessoiresFournisseurPage from "@/pages/AccessoiresFournisseurPage";
import OpportunitiesPage from "@/pages/OpportunitiesPage";
import OpportunityDetailPage from "@/pages/OpportunityDetailPage";
import CommercialReportPage from "@/pages/CommercialReportPage";

// Public Portal Pages
import PublicLayout from "@/pages/public/PublicLayout";
import PublicHomePage from "@/pages/public/PublicHomePage";
import PublicMachinesPage from "@/pages/public/PublicMachinesPage";
import PublicMachineDetailPage from "@/pages/public/PublicMachineDetailPage";
import PublicConfiguratorPage from "@/pages/public/PublicConfiguratorPage";
import PublicContactPage from "@/pages/public/PublicContactPage";
import PublicPartsPage from "@/pages/public/PublicPartsPage";
import PublicQuotePage from "@/pages/public/PublicQuotePage";
import PublicCMSPage from "@/pages/public/PublicCMSPage";
import PublicFinancingPage from "@/pages/public/PublicFinancingPage";
import PublicClientAreaPage from "@/pages/public/PublicClientAreaPage";
import PublicBlogPage from "@/pages/public/PublicBlogPage";
import PublicBlogArticlePage from "@/pages/public/PublicBlogArticlePage";
import PublicAccessoiresPage from "@/pages/public/PublicAccessoiresPage";
import LiuGongHistoryPage from "@/pages/public/LiuGongHistoryPage";

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" />;
  if (!isAdmin()) return <Navigate to="/" />;
  
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Routes>
      {/* Root redirects to portal */}
      <Route path="/" element={<Navigate to="/portail" replace />} />
      
      {/* Public Portal Routes - No authentication required */}
      <Route path="/portail" element={<PublicLayout />}>
        <Route index element={<PublicHomePage />} />
        <Route path="machines" element={<PublicMachinesPage />} />
        <Route path="machines/:id" element={<PublicMachineDetailPage />} />
        <Route path="configurateur" element={<PublicConfiguratorPage />} />
        <Route path="contact" element={<PublicContactPage />} />
        <Route path="devis" element={<PublicQuotePage />} />
        <Route path="financement" element={<PublicFinancingPage />} />
        <Route path="mon-compte" element={<PublicClientAreaPage />} />
        <Route path="blog" element={<PublicBlogPage />} />
        <Route path="blog/:slug" element={<PublicBlogArticlePage />} />
        <Route path="accessoires" element={<PublicAccessoiresPage />} />
        <Route path="parts" element={<PublicPartsPage />} />
        <Route path="parts/:brand" element={<PublicPartsPage />} />
        <Route path="histoire-liugong" element={<LiuGongHistoryPage />} />
        <Route path=":slug" element={<PublicCMSPage />} />
      </Route>

      {/* CRM/Admin Routes - Employee authentication required */}
      <Route path="/admin/login" element={user ? <Navigate to="/admin" /> : <LoginPage />} />
      <Route path="/login" element={<Navigate to="/admin/login" replace />} />
      <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/forgot-password" element={<Navigate to="/admin/forgot-password" replace />} />
      <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
      <Route path="/reset-password" element={<Navigate to="/admin/reset-password" replace />} />
      
      <Route path="/admin" element={
        <PrivateRoute>
          <Layout />
        </PrivateRoute>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/:id" element={<ClientDetailPage />} />
        <Route path="catalogue" element={<CataloguePage />} />
        <Route path="devis" element={<DevisListPage />} />
        <Route path="devis/nouveau" element={<DevisCreateSimplePage />} />
        <Route path="devis/nouveau-configurateur" element={<DevisCreatePage />} />
        <Route path="devis/import" element={<DevisImportPage />} />
        <Route path="devis/:id" element={<DevisDetailPage />} />
        <Route path="devis/:id/modifier" element={<DevisEditPage />} />
        <Route path="utilisateurs" element={
          <AdminRoute>
            <UsersPage />
          </AdminRoute>
        } />
        <Route path="roles" element={
          <AdminRoute>
            <RolesPermissionsPage />
          </AdminRoute>
        } />
        <Route path="accessoires-fournisseur" element={
          <AdminRoute>
            <AccessoiresFournisseurPage />
          </AdminRoute>
        } />
        <Route path="opportunities" element={
          <AdminRoute>
            <OpportunitiesPage />
          </AdminRoute>
        } />
        <Route path="opportunities/:id" element={
          <AdminRoute>
            <OpportunityDetailPage />
          </AdminRoute>
        } />
        <Route path="cabinets-snc" element={
          <AdminRoute>
            <CabinetsSNCPage />
          </AdminRoute>
        } />
        <Route path="fournisseurs" element={
          <AdminRoute>
            <FournisseursPage />
          </AdminRoute>
        } />
        <Route path="email-templates" element={
          <AdminRoute>
            <EmailTemplatesPage />
          </AdminRoute>
        } />
        <Route path="pieces-requises" element={
          <AdminRoute>
            <PiecesRequisesPage />
          </AdminRoute>
        } />
        <Route path="rapports/activites" element={<ActivityReportPage />} />
        <Route path="rapport-commercial" element={<CommercialReportPage />} />
        <Route path="messagerie" element={<MessagingPage />} />
        <Route path="calendrier" element={<CalendarPage />} />
        <Route path="taches" element={<TasksPage />} />
        <Route path="financement" element={<FinancingPage />} />
        <Route path="sav" element={<SAVPage />} />
        <Route path="mon-profil" element={<MyProfilePage />} />
        <Route path="mediatheque" element={<MediaLibraryPage />} />
        <Route path="ai-lead" element={<AILeadPage />} />
        <Route path="blog" element={<BlogPage />} />
        <Route path="emailing" element={<EmailingPage />} />
        <Route path="emailing/automation" element={<AutomationRulesPage />} />
        <Route path="emailing/:id" element={<EmailEditorPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="suivi-commercial" element={<ParcoursDashboardPage />} />
        <Route path="parametres" element={
          <AdminRoute>
            <SettingsPage />
          </AdminRoute>
        } />
        <Route path="site-web" element={
          <AdminRoute>
            <WebsiteSettingsPage />
          </AdminRoute>
        } />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
